import { Coordinate } from 'ol/coordinate';
import { Mission, Waypoint } from '../db';
import { SamplingSpec } from '../db';
import { SoilCore } from '../db';
import { Sample } from '../db';
import LineString from 'ol/geom/LineString';
import { CoordinatePoint, IntermediateWaypointsMap } from '../types/types';
import { ft_to_m, CORE_STOP_MAX_DISTANCE_FT, MAX_DISTANCE_BETWEEN_CLOSE_CORES_FT } from '../constants';
import {
  add_stop_core_waypoint,
  computeExtrasDistribution,
  create_new_core_with_waypoint_and_core_point,
  deleteExtraCoresForSamples,
  SampleExtrasDistribution,
} from '../db_ops/mission_ops';
import { calcLineDistance } from '../utils';
import { areCoresCoordinatesClose, generateCoreIdentifierFromCoordinates, parseCoreIdentifier } from './paths/utils';

// Smart-path tour application no longer flows through this generator —
// MissionClass.buildSmartPath bypasses calculate_path entirely and calls
// applyZoneSmartPathTourToExistingCores directly. This generator only
// handles the non-smart-path zone cases (Update Path, skip/unskip).
export class ZonePathGenerator {
  private waypointIndex: number = 1;
  private prevCoreIdentifier: string = '';
  private coresInfo: { [sampleId: string]: SampleExtrasDistribution } = {};

  constructor(
    private mission: Mission,
    private spec: SamplingSpec,
    protected cores: SoilCore[],
    private intermediateWaypointsMap: IntermediateWaypointsMap = {},
    private preserveExtras: boolean = false,
  ) {
    this.addIntermediateWaypoints = this.addIntermediateWaypoints.bind(this);
  }

  public async generate(): Promise<void> {
    if (this.preserveExtras) {
      // Skip/unskip: rebuild waypoints for ALL existing cores (including extras)
      this.deleteAllWaypoints();
      const allCores = this.cores.filter((c) => c.isPending());
      await this.rebuildWaypointsOnly(allCores);
    } else {
      // Update Path: delete extras, redistribute evenly (current behavior)
      const samples = this.getSortedSamples();

      const deletedCoresInstanceIds = deleteExtraCoresForSamples(samples);
      this.deleteAllWaypoints();

      const remainingCores = this.cores
        .filter((core) => !deletedCoresInstanceIds.includes(core.instance_id))
        .filter((core) => core.isPending());

      this.generateCoresInfo(samples);

      await this.processCoresAddingExtraIfNecessary(remainingCores);
    }
  }

  private async rebuildWaypointsOnly(cores: SoilCore[]) {
    // Cores arrive pre-sorted from calculate_path_restore_cores:
    // coresFromWaypoints (by waypoint_number) + missingCores (by waypoint_index)
    for (const core of cores) {
      this.addIntermediateWaypoints(this.generateCoreIdentifier(core));
      await this.addWaypointAndCorePoint(core);
      this.waypointIndex++;
      this.prevCoreIdentifier = this.generateCoreIdentifier(core);
    }
  }

  private async processCoresAddingExtraIfNecessary(cores: SoilCore[]) {
    // iterate through all re_ordered_cores as "core stops" since we deleted all close cores already
    for (const core of cores) {
      const previousPoint = this.choosePreviousPoint();
      await this.processCore(core, previousPoint, core.getSample()!);
    }
  }

  protected getSortedSamples(): Sample[] {
    return this.mission.getAllSamples();
  }

  protected deleteAllWaypoints(): void {
    const waypoints = this.mission.getWaypoints();

    for (const wp of waypoints) {
      wp.dispose();
    }
  }

  protected choosePreviousPoint(): CoordinatePoint {
    // If we can find a previous waypoint, use it.
    const previousWaypoint = this.mission
      .getWaypoints()
      .find((wp: Waypoint) => wp.waypoint_number === this.waypointIndex - 1);

    if (previousWaypoint) {
      return previousWaypoint as CoordinatePoint;
    }

    const missionStartingPosition = this.mission.getStartingPosition();

    const startingPositionPoint: CoordinatePoint = {
      lat: missionStartingPosition.lat,
      lon: missionStartingPosition.lon,
      getCoordinates: () => [missionStartingPosition.lat, missionStartingPosition.lon],
    };

    return startingPositionPoint;
  }

  protected async processCore(core: SoilCore, prevPoint: CoordinatePoint, sample: Sample) {
    const lineString = new LineString([
      [core.lat, core.lon],
      [prevPoint.lat, prevPoint.lon],
    ]);

    const lineDistance = calcLineDistance(prevPoint, core);
    const oneMore = this.coresInfo[sample.sample_id].remainder > 0 ? 1 : 0;

    // Add cores starting from the farthest point on the line and
    // ending one segment before the original core, so that
    // the original core is added lastly on its original place.
    for (let j = this.coresInfo[sample.sample_id].coresNumberToAddToAllOriginalCores + oneMore; j > 0; j--) {
      this.addExtraCore(
        lineString,
        j,
        lineDistance,
        sample,
        // We only want to look for intermediate waypoints before we add a first extra core
        j === this.coresInfo[sample.sample_id].coresNumberToAddToAllOriginalCores + oneMore,
      );
    }

    this.coresInfo[sample.sample_id].remainder--;

    // Add intermediate waypoints between regular (non-extra) cores (if any).
    this.addIntermediateWaypoints(this.generateCoreIdentifier(core));

    // Add waypoint to the original core.
    await this.addWaypointAndCorePoint(core);
    this.waypointIndex++;
    this.prevCoreIdentifier = this.generateCoreIdentifier(core);
  }

  protected generateCoresInfo(samples: Sample[]): void {
    this.coresInfo = computeExtrasDistribution(samples, this.spec.cores);
  }

  private findIntermediateWaypoints(prevCoreIdentifier: string, currentCoreIdentifier: string): Coordinate[] {
    const maxDistance = ft_to_m(MAX_DISTANCE_BETWEEN_CLOSE_CORES_FT);

    const { sampleId: prevCoreSampleId, lat: prevCoreLat, lon: prevCoreLon } = parseCoreIdentifier(prevCoreIdentifier);

    const { sampleId: curCoreSampleId, lat: curCoreLat, lon: curCoreLon } = parseCoreIdentifier(currentCoreIdentifier);

    for (const coreFromIdentifier of Object.keys(this.intermediateWaypointsMap)) {
      if (!coreFromIdentifier.startsWith(`${prevCoreSampleId}_`)) {
        continue;
      }

      const interFromKeys = Object.keys(this.intermediateWaypointsMap[coreFromIdentifier]);

      if (interFromKeys.length === 0) {
        continue;
      }

      const coreToIdentifier = interFromKeys[0];

      if (!coreToIdentifier || !coreToIdentifier.startsWith(`${curCoreSampleId}_`)) {
        continue;
      }

      const { lat: coreFromLat, lon: coreFromLon } = parseCoreIdentifier(coreFromIdentifier);

      if (!areCoresCoordinatesClose(maxDistance, [prevCoreLat, prevCoreLon], [coreFromLat, coreFromLon])) {
        continue;
      }

      const { lat: coreToLat, lon: coreToLon } = parseCoreIdentifier(coreToIdentifier);

      if (!areCoresCoordinatesClose(maxDistance, [curCoreLat, curCoreLon], [coreToLat, coreToLon])) {
        continue;
      }

      return this.intermediateWaypointsMap[coreFromIdentifier][coreToIdentifier] || [];
    }

    return [];
  }

  private getIntermediateWaypointsBidirectional(
    prevCoreIdentifier: string,
    currentCoreIdentifier: string,
  ): Coordinate[] {
    let waypoints = this.findIntermediateWaypoints(prevCoreIdentifier, currentCoreIdentifier);

    if (waypoints.length === 0) {
      // We are reversing the waypoints here because we are going in the opposite direction
      waypoints = this.findIntermediateWaypoints(currentCoreIdentifier, prevCoreIdentifier).reverse();
    }

    return waypoints;
  }

  private addIntermediateWaypoints(currentCoreIdentifier: string): number {
    if (Object.keys(this.intermediateWaypointsMap).length === 0) {
      return this.waypointIndex;
    }

    if (!this.prevCoreIdentifier || !currentCoreIdentifier) {
      return this.waypointIndex;
    }

    const intermediateWaypoints = this.getIntermediateWaypointsBidirectional(
      this.prevCoreIdentifier,
      currentCoreIdentifier,
    );
    intermediateWaypoints.forEach((point: Coordinate) => {
      this.mission.addWaypoint(point[0], point[1]);
      this.waypointIndex++;
    });

    return this.waypointIndex;
  }

  private generateCoreIdentifier(core: SoilCore): string {
    return generateCoreIdentifierFromCoordinates(core.sample_id!, [core.lat, core.lon]);
  }

  private addExtraCore(
    lineString: LineString,
    j: number,
    lineDistance: number,
    sample: Sample,
    checkForIntermediate: boolean,
  ) {
    const latLon = lineString.getCoordinateAt((j * ft_to_m(CORE_STOP_MAX_DISTANCE_FT)) / lineDistance);
    const createdCore = create_new_core_with_waypoint_and_core_point(
      latLon,
      this.mission.instance_id,
      sample,
      this.waypointIndex,
      'Rogo Extra Core',
      checkForIntermediate
        ? () => {
            return this.addIntermediateWaypoints(generateCoreIdentifierFromCoordinates(sample.sample_id, latLon));
          }
        : undefined,
    );

    this.waypointIndex++;
    this.prevCoreIdentifier = createdCore.sample_id + `_${createdCore.lat}_${createdCore.lon}`;
  }

  private async addWaypointAndCorePoint(core: SoilCore): Promise<void> {
    await add_stop_core_waypoint(core, this.mission.instance_id, this.waypointIndex, core.core_number);
  }
}
